import type { ReactNode } from 'react';
import { FotoPrivada } from '../../../components/DetalleExpandible';
import type { Pregunta } from '../../../domain/yichang/catalogo';
import {
  codigoDeClave, comoFoto, esListaDeFotos, etiquetaOpcion, humanizarClave, vacia,
} from '../../../domain/yichang/respuestas';
import { obj } from '../../../domain/yichang/tipos';

/**
 * Las piezas con que se dibuja cualquier respuesta de una ficha de Yichang.
 *
 * 🔴 `Valor` sabe mostrar **cualquier** cosa que traiga el jsonb —texto, número, sí/no, lista, foto,
 * objeto anidado— y `Respuestas` muestra también las llaves que el catálogo no conoce. Es a
 * propósito: la ficha se guarda entera, sin lista cerrada, y si el formulario agrega una pregunta
 * mañana, la bandeja tiene que mostrarla hoy, aunque sea con un rótulo armado de su nombre. Lo que
 * falta en el catálogo se ve peor, no desaparece.
 */

/**
 * Una respuesta en blanco. «En blanco» y no «sin responder»: el formulario deja en blanco tanto lo que
 * no se contestó como lo que no correspondía preguntar, y desde la ficha no siempre se distinguen.
 */
export const SIN = <span className="text-gray-400">en blanco</span>;

export const soles = (n: number | null): string => (n === null ? '—' : `S/ ${n.toFixed(2)}`);

/** Las fotos de una lista, firmadas recién al montarse (la firma dura 60 s). */
export function Fotos({ lista, alt }: { lista: readonly unknown[]; alt: string }) {
  const fotos = lista.map(comoFoto).filter((x) => x !== null);
  if (fotos.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-3">
      {fotos.map((f, i) => ('ruta' in f
        ? <FotoPrivada key={f.ruta} path={f.ruta} alt={`${alt} ${i + 1}`} />
        : (
          <p key={`fallida-${i}`} className="rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
            Se tomó una foto y no se pudo subir{f.fallida !== null ? `: ${f.fallida}` : '.'}
          </p>
        )))}
    </div>
  );
}

/** Un valor cualquiera del jsonb, legible. `clave` es la llave de la pregunta, para rotular opciones. */
export function Valor({ v, clave, alt = 'Foto' }: { v: unknown; clave?: string; alt?: string }): ReactNode {
  if (vacia(v)) return SIN;
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  if (typeof v === 'number') return <span className="tabular-nums">{v.toLocaleString('es-PE')}</span>;
  if (typeof v === 'string') {
    const f = comoFoto(v);
    if (f !== null) return <Fotos lista={[v]} alt={alt} />;
    return etiquetaOpcion(v, clave);
  }
  if (Array.isArray(v)) {
    if (esListaDeFotos(v)) return <Fotos lista={v} alt={alt} />;
    if (v.every((x) => typeof x === 'string' || typeof x === 'number')) {
      return (
        <span className="flex flex-wrap gap-1">
          {v.map((x, i) => (
            <span key={i} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-800">
              {typeof x === 'string' ? etiquetaOpcion(x, clave) : x}
            </span>
          ))}
        </span>
      );
    }
    return (
      <ol className="space-y-2">
        {v.map((x, i) => (
          <li key={i} className="rounded border border-gray-200 p-2">
            <p className="mb-1 text-xs font-semibold text-gray-600">{i + 1}</p>
            <Valor v={x} alt={`${alt} ${i + 1}`} />
          </li>
        ))}
      </ol>
    );
  }
  if (comoFoto(v) !== null) return <Fotos lista={[v]} alt={alt} />;
  const o = obj(v);
  if (o !== null) return <Objeto o={o} />;
  return String(v);
}

/** Un objeto anidado: cada llave con su rótulo. `rotulos` pone nombre a las conocidas. */
export function Objeto({ o, rotulos = {}, omitir = [] }: {
  o: Record<string, unknown>;
  rotulos?: Record<string, string | Pregunta>;
  omitir?: readonly string[];
}) {
  const llaves = Object.keys(o).filter((k) => !omitir.includes(k));
  if (llaves.length === 0) return SIN;
  return (
    <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
      {llaves.map((k) => {
        const r = rotulos[k];
        const [num, texto] = typeof r === 'string' ? [null, r] : (r ?? [codigoDeClave(k), humanizarClave(k)]);
        return <Fila key={k} num={num} texto={texto}><Valor v={o[k]} clave={k} alt={texto} /></Fila>;
      })}
    </div>
  );
}

/** Una pregunta con su número del cuestionario, y la respuesta abajo. */
export function Fila({ num, texto, children }: { num: string | null; texto: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-gray-500">
        {num !== null && (
          <span className="mr-1 rounded bg-gray-100 px-1 font-mono text-[11px] text-gray-700">{num}</span>
        )}
        {texto}
      </div>
      <div className="text-sm text-gray-900">{children}</div>
    </div>
  );
}

/**
 * Las respuestas de un objeto de la ficha, en el orden del catálogo y **con las llaves que el
 * catálogo no conoce al final**, rotuladas con su nombre. `omitir` saca las que se dibujan aparte
 * o son metadato.
 *
 * Una llave del catálogo que la ficha no trae no se dibuja: no es «sin respuesta», es una pregunta
 * que el formulario no llegó a hacer (una góndola cerrada solo guarda cuatro campos). Tampoco las que
 * quedaron en blanco: ver abajo.
 */
export function Respuestas({ o, preguntas, omitir = [], ancho = false }: {
  o: Record<string, unknown> | null;
  preguntas: Record<string, Pregunta>;
  omitir?: readonly string[];
  ancho?: boolean;
}) {
  if (o === null) return null;
  // Las que quedaron en blanco no se dibujan: casi siempre son preguntas que el formulario escondió
  // (y vació) porque no correspondían. Las obligatorias que faltaron las lista la propia ficha.
  const conocidas = Object.keys(preguntas).filter((k) => k in o && !omitir.includes(k) && !vacia(o[k]));
  const nuevas = Object.keys(o).filter((k) => !(k in preguntas) && !omitir.includes(k) && !vacia(o[k]));
  if (conocidas.length + nuevas.length === 0) return null;
  return (
    <div className={`grid gap-x-6 gap-y-2 ${ancho ? '' : 'sm:grid-cols-2'}`}>
      {conocidas.map((k) => {
        const [num, texto] = preguntas[k];
        return <Fila key={k} num={num} texto={texto}><Valor v={o[k]} clave={k} alt={texto} /></Fila>;
      })}
      {nuevas.map((k) => (
        <Fila key={k} num={codigoDeClave(k)} texto={humanizarClave(k)}>
          <Valor v={o[k]} clave={k} alt={humanizarClave(k)} />
        </Fila>
      ))}
    </div>
  );
}

/** Una pastilla del estado de presencia de una marca. El color no va solo: siempre con palabras. */
export function PastillaPresencia({ p }: { p: 'presente' | 'ausente' | 'no_se_sabe' }) {
  const [cls, txt] = {
    presente: ['bg-green-100 text-green-800', 'se encontró'],
    ausente: ['bg-gray-200 text-gray-800', 'no se encontró'],
    no_se_sabe: ['bg-amber-100 text-amber-900', 'no se puede saber'],
  }[p];
  return <span className={`rounded px-1.5 py-0.5 text-xs ${cls}`}>{txt}</span>;
}
